/**
 * ApprovalPanel — Phase 6: Review results and approve/reject the optimization run.
 * Shows delta comparison against the previous approved run.
 */

import React, { useState, useEffect, useMemo } from 'react';
import * as api from '../services/routeOptimizerService';

export default function ApprovalPanel({ weekId, details }) {
  const [approval, setApproval] = useState(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [runHistory, setRunHistory] = useState([]);
  const [tierSummary, setTierSummary] = useState({});

  const resultTiers = details.result_tiers || [];
  const hasTiers = resultTiers.length > 0;

  // Load existing approval, run history, and tier summary for delta
  useEffect(() => {
    if (!weekId) return;
    api.getRunHistory(weekId).then(history => {
      setRunHistory(history || []);
      if (history && history.length > 0) {
        setApproval(history[0]);
      }
    }).catch(() => {});
    api.getResultsSummary(weekId).then(data => {
      setTierSummary(data.tiers || {});
    }).catch(() => {});
  }, [weekId]);

  // Find the previous approved run's stats for delta comparison
  const previousRun = useMemo(() => {
    const approved = runHistory.filter(r => r.status === 'approved' && r.total_miles);
    return approved.length > 0 ? approved[0] : null;
  }, [runHistory]);

  const currentBestMiles = useMemo(() => {
    const tiers = Object.values(tierSummary);
    if (tiers.length === 0) return null;
    return Math.min(...tiers.map(t => t.total_distance_miles || Infinity));
  }, [tierSummary]);

  const deltaInfo = useMemo(() => {
    if (!previousRun?.total_miles || !currentBestMiles) return null;
    const prev = Number(previousRun.total_miles);
    const diff = currentBestMiles - prev;
    const pct = prev > 0 ? ((diff / prev) * 100) : 0;
    return { prev, current: currentBestMiles, diff, pct };
  }, [previousRun, currentBestMiles]);

  const handleApprove = async (status) => {
    setSaving(true);
    setMessage('');
    try {
      // Extract active_technicians from the best tier for delta comparison
      const bestTier = Object.values(tierSummary).sort((a, b) =>
        (a.total_distance_miles || Infinity) - (b.total_distance_miles || Infinity)
      )[0];
      const activeTechs = bestTier?.active_technicians ?? null;

      await api.approveRun(weekId, {
        status,
        comment: comment.trim() || undefined,
        tiers: resultTiers,
        total_miles: currentBestMiles,
        active_technicians: activeTechs,
      });
      const newApproval = { status, comment: comment.trim(), timestamp: new Date().toISOString(), total_miles: currentBestMiles };
      setApproval(newApproval);
      setRunHistory(prev => [newApproval, ...prev]);
      setMessage(status === 'approved' ? 'Routes approved and saved.' : 'Run rejected. Go back to adjust and re-run.');
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!hasTiers) {
    return (
      <div className="ro-panel">
        <h3>Approve Routes</h3>
        <div className="ro-empty-state">
          <div className="ro-empty-state-icon">{'\u{1F4CB}'}</div>
          <h4 className="ro-empty-state-title">Nothing to Approve</h4>
          <p className="ro-empty-state-text">Run the optimizer and review results before approving routes for deployment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ro-panel">
      <h3>Approve Routes</h3>

      {/* Current status */}
      {approval && (
        <div className={`ro-status-card ${approval.status === 'approved' ? 'ro-status-card-approved' : approval.status === 'rejected' ? 'ro-status-card-rejected' : 'ro-status-card-default'}`}>
          <div className={`ro-status-card-title ${approval.status === 'approved' ? 'ro-status-text-approved' : 'ro-status-text-rejected'}`}>
            {approval.status === 'approved' ? '\u2713 Routes Approved' : '\u2717 Run Rejected'}
          </div>
          {approval.comment && (
            <div className="ro-status-card-comment">"{approval.comment}"</div>
          )}
          {approval.timestamp && (
            <div className="ro-status-card-timestamp">
              {new Date(approval.timestamp).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Delta comparison — first run message when no previous */}
      {!deltaInfo && currentBestMiles && !previousRun && (
        <div className="ro-section">
          <div className="ro-validation-success">
            <strong>First optimization run</strong>
            <span> — {currentBestMiles.toFixed(1)} total miles. This will be the baseline for future comparisons.</span>
          </div>
        </div>
      )}
      {deltaInfo && (
        <div className={`ro-delta-card ${deltaInfo.diff <= 0 ? 'ro-delta-card-positive' : 'ro-delta-card-negative'}`}>
          <h4 className="ro-subsection-title">Comparison to Previous Run</h4>
          <div className="ro-delta-grid">
            <div>
              <div className="ro-delta-label">Previous</div>
              <div className="ro-delta-value">{deltaInfo.prev.toFixed(1)} mi</div>
            </div>
            <div>
              <div className="ro-delta-label">Current Best</div>
              <div className="ro-delta-value ro-delta-value-highlight">{deltaInfo.current.toFixed(1)} mi</div>
            </div>
            <div>
              <div className="ro-delta-label">Change</div>
              <div className={`ro-delta-value ro-delta-change ${deltaInfo.diff <= 0 ? 'ro-delta-change-positive' : 'ro-delta-change-negative'}`}>
                <span className="ro-delta-arrow">{deltaInfo.diff < 0 ? '\u25BC' : deltaInfo.diff > 0 ? '\u25B2' : ''}</span>
                {deltaInfo.diff <= 0 ? '' : '+'}{deltaInfo.diff.toFixed(1)} mi
                <span className="ro-delta-pct">({deltaInfo.pct >= 0 ? '+' : ''}{deltaInfo.pct.toFixed(1)}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Result summary */}
      <div className="ro-section">
        <h4 className="ro-subsection-title">Completed Tiers</h4>
        <div className="ro-tag-row">
          {resultTiers.map(tier => (
            <span key={tier} className="ro-tag ro-tag-success">{tier}</span>
          ))}
        </div>
      </div>

      {/* Comment field */}
      <div className="ro-section">
        <label className="ro-form-label">
          Comment (optional)
        </label>
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value.slice(0, 500))}
          placeholder="Add notes about this optimization run..."
          className="ro-textarea"
          maxLength={500}
          aria-label="Comment about this optimization run"
        />
        {comment.length > 0 && (
          <div className={`ro-char-count${comment.length >= 480 ? ' ro-char-count-warn' : ''}`}>
            {comment.length}/500
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="ro-button-group ro-section">
        <button
          className="ro-btn ro-btn-primary ro-btn-lg"
          onClick={() => handleApprove('approved')}
          disabled={saving}
          aria-label="Approve routes and save as active plan"
        >
          {saving ? 'Saving...' : 'Approve Routes'}
        </button>
        <button
          className="ro-btn ro-btn-danger"
          onClick={() => handleApprove('rejected')}
          disabled={saving}
          aria-label="Reject run and go back to re-optimize"
        >
          Reject & Re-run
        </button>
      </div>

      {message && (
        <div className={message.startsWith('Error') ? 'ro-message-error' : 'ro-message-success'}>
          {message.replace(/^Error:\s*/, '')}
        </div>
      )}

      {/* Run History */}
      {runHistory.length > 0 && (
        <div className="ro-section-divider">
          <h4 className="ro-subsection-title">Run History</h4>
          <table className="ro-preview-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Status</th>
                <th>Tiers</th>
                <th className="ro-text-right">Miles</th>
                <th>Changes</th>
                <th>Comment</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runHistory.map((run, i) => (
                <tr key={i} className={i === 0 ? 'ro-row-latest' : ''}>
                  <td>{run.timestamp ? new Date(run.timestamp).toLocaleString() : '\u2014'}</td>
                  <td>
                    <span className={run.status === 'approved' ? 'ro-status-text-approved' : 'ro-status-text-rejected'}>
                      {run.status === 'approved' ? '\u2713 Approved' : '\u2717 Rejected'}
                    </span>
                  </td>
                  <td>
                    {run.tiers?.length > 0
                      ? run.tiers.map(t => <span key={t} className="ro-tag ro-tag-sm">{t}</span>)
                      : '\u2014'}
                  </td>
                  <td className="ro-text-tabular">
                    {run.total_miles ? Number(run.total_miles).toFixed(1) : '\u2014'}
                  </td>
                  <td className="ro-text-tabular">
                    {run.changes_count > 0 ? run.changes_count : '\u2014'}
                  </td>
                  <td className="ro-text-truncate" title={run.comment || ''}>
                    {run.comment || '\u2014'}
                  </td>
                  <td>
                    {run.status === 'approved' && run.snapshot_path && (
                      <button
                        className="ro-btn ro-btn-sm"
                        onClick={async () => {
                          try {
                            const ts = run.snapshot_path.split('/').pop();
                            const result = await api.exportWeek(weekId, { approval: ts });
                            if (result.url) window.open(result.url, '_blank');
                          } catch (err) {
                            console.error('Export failed:', err);
                          }
                        }}
                        aria-label="Export this approved run"
                      >
                        Export
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
